// Servicio para gestión de tratamientos médicos (API Backend)
import axios from 'axios';
import medicoService from './medicoService';
import config from '../config/api';

const API_BASE_URL = config.endpoints.tratamientos;

const tratamientoService = {
    /**
     * Crear un nuevo tratamiento con medicamentos opcionales
     * @param {Object} tratamientoData - Datos del tratamiento
     * @returns {Promise<Object>} Tratamiento creado
     */
    async createTratamiento(tratamientoData) {
        try {
            const payload = {
                SOCIA_ID: tratamientoData.SOCIA_ID || tratamientoData.socia_id,
                MEDICO_ID: tratamientoData.MEDICO_ID || tratamientoData.medico_id,
                TRATAMIENTO_FECHAINICIO: tratamientoData.TRATAMIENTO_FECHAINICIO || tratamientoData.TRATAMIENTO_FECHA_INICIO || tratamientoData.tratamiento_fechainicio,
                TRATAMIENTO_FECHAFIN: tratamientoData.TRATAMIENTO_FECHAFIN || tratamientoData.TRATAMIENTO_FECHA_FIN || tratamientoData.tratamiento_fechafin,
                TRATAMIENTO_OBSERVACION: tratamientoData.TRATAMIENTO_OBSERVACION || tratamientoData.tratamiento_observacion || '',
                MEDICAMENTOS: tratamientoData.MEDICAMENTOS || tratamientoData.medicamentos || []
            };

            console.log('Payload enviado al backend:', payload);
            const response = await axios.post(`${API_BASE_URL}/crear`, payload);
            
            return {
                success: true,
                data: response.data,
                message: 'Tratamiento creado exitosamente'
            };
        } catch (error) {
            console.error('Error al crear tratamiento:', error);
            console.error('Respuesta del servidor:', error.response?.data);
            return {
                success: false,
                data: null,
                message: error.response?.data?.message || 'Error al crear el tratamiento'
            };
        }
    },

    /**
     * Actualizar un tratamiento existente
     * @param {string} id - ID del tratamiento
     * @param {Object} tratamientoData - Datos actualizados
     * @returns {Promise<Object>} Tratamiento actualizado
     */
    async updateTratamiento(id, tratamientoData) {
        try {
            const payload = {
                TRATAMIENTO_ID: id,
                SOCIA_ID: tratamientoData.SOCIA_ID || tratamientoData.socia_id,
                TRATAMIENTO_FECHAINICIO: tratamientoData.TRATAMIENTO_FECHAINICIO || tratamientoData.TRATAMIENTO_FECHA_INICIO || tratamientoData.tratamiento_fechainicio,
                TRATAMIENTO_FECHAFIN: tratamientoData.TRATAMIENTO_FECHAFIN || tratamientoData.TRATAMIENTO_FECHA_FIN || tratamientoData.tratamiento_fechafin,
                TRATAMIENTO_OBSERVACION: tratamientoData.TRATAMIENTO_OBSERVACION || tratamientoData.tratamiento_observacion || '',
                MEDICO_ID: tratamientoData.MEDICO_ID || tratamientoData.medico_id,
                MEDICAMENTOS: tratamientoData.MEDICAMENTOS || tratamientoData.medicamentos || []
            };

            // ⚠️ IMPORTANTE: Incluir medicamentos eliminados si existen
            if (tratamientoData.MEDICAMENTOS_ELIMINADOS && tratamientoData.MEDICAMENTOS_ELIMINADOS.length > 0) {
                payload.MEDICAMENTOS_ELIMINADOS = tratamientoData.MEDICAMENTOS_ELIMINADOS;
            }

            const response = await axios.put(`${API_BASE_URL}/actualizar/${id}`, payload);
            
            return {
                success: true,
                data: response.data,
                message: 'Tratamiento actualizado exitosamente'
            };
        } catch (error) {
            console.error('Error al actualizar tratamiento:', error);
            return {
                success: false,
                data: null,
                message: error.response?.data?.message || 'Error al actualizar el tratamiento'
            };
        }
    },

    /**
     * Obtener todos los tratamientos de una socia
     * @param {string} sociaId - ID de la socia
     * @returns {Promise<Object>} Lista de tratamientos
     */
    async getTratamientosBySocia(sociaId) {
        try {
            const response = await axios.get(`${API_BASE_URL}/socia/${sociaId}`);
            
            return {
                success: true,
                data: response.data,
                message: 'Tratamientos obtenidos exitosamente'
            };
        } catch (error) {
            console.error('Error al obtener tratamientos:', error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.message || 'Error al obtener tratamientos'
            };
        }
    },

    /**
     * Eliminar un tratamiento completo
     * @param {string} id - ID del tratamiento
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteTratamiento(id) {
        try {
            const response = await axios.delete(`${API_BASE_URL}/eliminar/${id}`);
            
            return {
                success: true,
                data: response.data,
                message: 'Tratamiento eliminado exitosamente'
            };
        } catch (error) {
            console.error('Error al eliminar tratamiento:', error);
            return {
                success: false,
                data: null,
                message: error.response?.data?.message || 'Error al eliminar el tratamiento'
            };
        }
    },

    /**
     * Actualizar un medicamento específico de un tratamiento
     * @param {string} tratamientoId - ID del tratamiento
     * @param {Object} medicamentoData - Datos del medicamento
     * @returns {Promise<Object>} Medicamento actualizado
     */
    async updateMedicamento(tratamientoId, medicamentoData) {
        try {
            const payload = {
                medicamento_id: medicamentoData.medicamento_id || medicamentoData.MEDICAMENTO_ID,
                medicamento_nombre: medicamentoData.medicamento_nombre || medicamentoData.MEDICAMENTO_NOMBRE,
                medicamento_cantidad: medicamentoData.medicamento_cantidad || medicamentoData.MEDICAMENTO_CANTIDAD
            };

            const response = await axios.put(`${API_BASE_URL}/medicamento/actualizar/${tratamientoId}`, payload);
            
            return {
                success: true,
                data: response.data,
                message: 'Medicamento actualizado exitosamente'
            };
        } catch (error) {
            console.error('Error al actualizar medicamento:', error);
            return {
                success: false,
                data: null,
                message: error.response?.data?.message || 'Error al actualizar el medicamento'
            };
        }
    },

    /**
     * Agregar un nuevo medicamento a un tratamiento existente
     * @param {string} tratamientoId - ID del tratamiento
     * @param {Object} medicamentoData - Datos del medicamento
     * @returns {Promise<Object>} Medicamento agregado
     */
    async addMedicamento(tratamientoId, medicamentoData) {
        try {
            const payload = {
                medicamento_nombre: medicamentoData.medicamento_nombre || medicamentoData.MEDICAMENTO_NOMBRE,
                medicamento_cantidad: medicamentoData.medicamento_cantidad || medicamentoData.MEDICAMENTO_CANTIDAD
            };

            const response = await axios.post(`${API_BASE_URL}/medicamento/agregar/${tratamientoId}`, payload);
            
            return {
                success: true,
                data: response.data,
                message: 'Medicamento agregado exitosamente'
            };
        } catch (error) {
            console.error('Error al agregar medicamento:', error);
            return {
                success: false,
                data: null,
                message: error.response?.data?.message || 'Error al agregar el medicamento'
            };
        }
    },

    // getTratamientos: compatibilidad + soporte paginado opcional
    // - Si no se pasan `page`, devuelve el arreglo completo (legacy)
    // - Si se pasa `page`, realiza la llamada paginada al backend
    async getTratamientos(page = undefined, pageSize = 10, nombre = '') {
        // Si no se solicitó paginación, mantener comportamiento legacy
        if (page === undefined || page === null) {
            return await this.getAllTratamientos();
        }

        // Petición paginada
        try {
            const params = `?page=${page}&pageSize=${pageSize}${nombre ? `&nombre=${encodeURIComponent(nombre)}` : ''}`;
            const response = await axios.get(`${API_BASE_URL}/todos${params}`);

            return {
                success: true,
                data: response.data,
                message: 'Tratamientos obtenidos exitosamente'
            };
        } catch (error) {
            console.error('Error al obtener tratamientos paginados:', error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.message || 'Error al obtener los tratamientos'
            };
        }
    },

    async getAllTratamientos() {
        try {
            const response = await axios.get(`${API_BASE_URL}/todos`);
            
            return {
                success: true,
                data: response.data,
                message: 'Tratamientos obtenidos exitosamente'
            };
        } catch (error) {
            console.error('Error al obtener todos los tratamientos:', error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.message || 'Error al obtener los tratamientos'
            };
        }
    },

    async getTratamientoById(_id) {
        // Este método requeriría un endpoint específico en el backend
        // Por ahora retorna un placeholder
        return {
            success: false,
            data: null,
            message: 'Método no implementado. Use getTratamientosBySocia'
        };
    },

    // Métodos de compatibilidad
    async getTratamientosByPaciente(pacienteId) {
        return await this.getTratamientosBySocia(pacienteId);
    },

    async getTratamientosByMedico(_medicoId) {
        // Requiere endpoint específico en backend
        return {
            success: false,
            data: [],
            message: 'Método no implementado en el backend'
        };
    },

    async getMedicosDisponibles() {
        // Delegar al servicio de médicos
        try {
            const medicos = await medicoService.getMedicosDisponibles();
            return {
                success: true,
                data: medicos,
                message: 'Médicos obtenidos exitosamente'
            };
        } catch (error) {
            console.error('Error al obtener médicos:', error);
            return {
                success: false,
                data: [],
                message: 'Error al obtener médicos disponibles'
            };
        }
    },

    async getPacientesDisponibles() {
        // Estos datos deberían venir del servicio de socias
        // Por ahora retorna un mensaje indicando que use sociasStore
        return {
            success: false,
            data: [],
            message: 'Use sociasStore para obtener la lista de socias'
        };
    },

    
};

export default tratamientoService;